import {
  computeClientDebt,
  convertEurToChf,
  deriveInvoiceStatus,
  invoiceOutstandingChf,
  pensionFundPct,
  prepaymentFor,
  resolvePlanForTier,
  revenueMonthTotals,
  type PricingPlan,
  type PricingPlanTier,
  type PricingRuleSet,
} from '@carsfinders/contracts';

import type {
  FinanceClient,
  FinanceClientStatus,
  FinanceInvoiceStatus,
  FinancePeriod,
  FinanceReceivable,
  FinanceTab,
  FinanceTransaction,
  Invoice,
  InvoiceKind,
  InvoiceLine,
  InvoiceLot,
  MonthKey,
  ReceivableTone,
  RevenueMonth,
  TransactionKind,
} from './types';

/**
 * Pure helpers for the «Фінанси» screen — money formatting, balance folding,
 * tab filtering and the revenue bar geometry. No React, no network; unit-tested
 * in utils.test.ts.
 *
 * ── What changed at the phase-6 cutover ─────────────────────────────────────
 * The contract carries RAW VALUES ONLY: `amountChf: number` where the fixtures
 * held a pre-rendered `'2 170'` string, `dueAt: Date` where they held `'22.09'`, `month: '2026-09'`
 * where they held `'Вер'`. Everything that used to arrive pre-rendered is
 * formatted here and rendered in the components, and the two values that used
 * to arrive pre-computed — an invoice's `overdue` flag and a receivable's
 * urgency `tone` — are DERIVED AT RENDER from `dueAt` against now. A
 * server-computed "5 days late" is stale the moment the response is cached, and
 * the two copies disagree on the far side of midnight.
 *
 * ── CHF end to end (owner rule 01) ──────────────────────────────────────────
 * Deposits, invoices, receivables and revenue are all CHF, and {@link formatChf}
 * is the one money formatter for them. The journal is the exception, and an
 * honest one: a ledger row is `CHF|EUR|USD|UAH` by schema and carries its own
 * `currency`, so {@link formatMoney} prints that unit rather than assuming one.
 * A EUR-priced figure (the service fee, a plan's `monthlyFee`) reaches a CHF
 * line only through `convertEurToChf`, with the conversion recorded beside it.
 */

/** Join truthy class names (feature-local stand-in for the web `cn`). */
export function cn(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

/* ────────────────────────────── Formatting ───────────────────────────────── */

/**
 * `12 400` — uk-UA grouping with the non-breaking space flattened to a plain
 * one, byte-for-byte what the artboard's `fmt` produces. The flattening is not
 * cosmetic: an NBSP and a space render at different widths, so keeping the
 * artboard's exact substitution is what makes the two measurable against
 * each other.
 */
export function formatNumber(value: number): string {
  return value.toLocaleString('uk-UA').replace(/\u00a0/g, ' ');
}

/**
 * `7 052` / `7 052,40` — a money magnitude by the D-9 rule: whole units print
 * without cents, anything else with exactly two decimals. The locale alone
 * would drop a trailing zero («7 052,4»), which reads like a typo on money.
 * Rounded to the cent first, so float noise never prints as a third decimal.
 */
function formatAmount(value: number): string {
  const cents = Math.round(value * 100);
  const abs = Math.abs(cents);
  const fraction = abs % 100;
  const whole = formatNumber(Math.floor(abs / 100));
  return `${cents < 0 ? '-' : ''}${whole}${fraction ? `,${String(fraction).padStart(2, '0')}` : ''}`;
}

/**
 * `CHF 12 400` / `CHF 250,60` — THE money formatter of admin. Every figure on
 * the finance screen is CHF (owner rule 01); only a journal row may name
 * another unit, and it does so through {@link formatMoney}.
 */
export function formatChf(value: number): string {
  return `CHF ${formatAmount(value)}`;
}

/**
 * A journal amount with its own unit — the ledger is `CHF|EUR|USD|UAH` by
 * schema, so a suffix cannot be assumed and the row carries `currency`. The
 * ISO code is printed for every unit, CHF included, so a EUR ledger row can
 * never be mistaken for a franc figure by its symbol.
 */
export function formatMoney(value: number, currency: string): string {
  return `${currency} ${formatAmount(value)}`;
}

/** `CHF 300`, or an em dash for zero — the table prints absence, not `CHF 0`. */
export function chfOrDash(value: number): string {
  return value ? formatChf(value) : '—';
}

/** `22.09` — the `dd.MM` every date cell on this screen prints. */
export function formatDayMonth(date: Date): string {
  return new Intl.DateTimeFormat('uk-UA', { day: '2-digit', month: '2-digit' }).format(date);
}

/**
 * `'2026-09'` → `{ year: 2026, month: 9 }`.
 *
 * A month is SPLIT rather than formatted here on purpose. The wire value is a
 * `YYYY-MM` key with no timezone (a revenue bucket is a month, not an instant),
 * and the caption for it is a catalogue entry — `adminFinance.month.9` — so
 * that an English operator reads "September" and a Ukrainian one «Вересень».
 * Formatting it in this file would bake one language into a pure function and
 * put it beyond the parity check.
 *
 * Returns `null` for anything that is not a `YYYY-MM` key, so a malformed
 * bucket renders as the raw key rather than as month 0 of year NaN.
 */
export function monthKeyParts(month: MonthKey): { year: number; month: number } | null {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(month);
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]) };
}

/**
 * Ukrainian plural form for a count: 1 рахунок / 2 рахунки / 5 рахунків.
 *
 * The artboard hardcodes the form that happened to fit its mock numbers
 * («3 клієнти», «5 клієнтів», «1 рахунок»). Real counts move, and a screen that
 * says "2 клієнт" reads as broken, so the form is computed.
 */
export function pluralUk(count: number, forms: { one: string; few: string; many: string }): string {
  const mod10 = Math.abs(count) % 10;
  const mod100 = Math.abs(count) % 100;
  if (mod10 === 1 && mod100 !== 11) return forms.one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms.few;
  return forms.many;
}

/**
 * `10` from 0.1, `3,5` from 0.035 — a rule-set rate as the percentage a label
 * prints. Rounded to one decimal, which is the finest the statute uses.
 */
export function formatPct(rate: number): string {
  return formatNumber(Math.round(rate * 1000) / 10);
}

/* ─────────────────────── Derived-at-render invoice facts ─────────────────── */

/**
 * The prefix `ClientRollupService` marks a DERIVED invoice row with.
 *
 * Re-implemented here rather than imported: `isProvisionalInvoiceNo` lives in
 * `apps/api/src/modules/admin-finance/client-rollup.service.ts`, and the admin
 * cannot import `apps/api/src/modules/*` — cross-app code goes through
 * `@carsfinders/*`. The prefix itself is part of the wire contract of
 * `GET /admin/finance/clients`, which is why a local copy is safe where a local
 * copy of `deriveInvoiceStatus` would not be: this is a string constant, not a
 * rule that can drift into charging a client differently.
 */
export const PROVISIONAL_INVOICE_PREFIX = 'PROV-';

/**
 * True when this "invoice" is a rollup placeholder derived from an invoiced
 * deal rather than a document somebody issued.
 *
 * Its `no` is `PROV-<dealId>` and it must NEVER be shown to an operator as a
 * quotable invoice number: a client asked to pay against `PROV-68f3aa…` is
 * being given a reference that exists nowhere, and its due date is the
 * platform's internal SLA clock rather than a term anyone agreed to.
 */
export function isProvisionalInvoiceNo(no: string): boolean {
  return no.startsWith(PROVISIONAL_INVOICE_PREFIX);
}

/** Whole days `dueAt` is past `now`; 0 when it is not past at all. */
export function overdueDays(dueAt: Date, now: Date = new Date()): number {
  const elapsed = now.getTime() - dueAt.getTime();
  return elapsed > 0 ? Math.floor(elapsed / 86_400_000) : 0;
}

/** How many days until `dueAt`; negative once it is past. */
export function daysUntil(dueAt: Date, now: Date = new Date()): number {
  return Math.ceil((dueAt.getTime() - now.getTime()) / 86_400_000);
}

/**
 * Urgency ramp on the upcoming-receivables list, from `dueAt` against now.
 *
 * Past due is `danger`, inside three days is `warn`, anything further out is
 * neutral. Derived rather than carried: see the note on
 * `FinanceReceivableSchema` in contracts.
 */
export function receivableTone(dueAt: Date, now: Date = new Date()): ReceivableTone {
  const days = daysUntil(dueAt, now);
  if (days < 0) return 'danger';
  if (days <= 3) return 'warn';
  return 'neutral';
}

/**
 * What is still outstanding on one invoice: total − prepayment − paid. The
 * contract's own definition, so this screen and the API rollup cannot disagree.
 */
export function invoiceOwed(invoice: Invoice): number {
  return invoiceOutstandingChf(invoice);
}

/**
 * What has landed against one invoice: payments PLUS the deposit applied as
 * prepayment at issue — both are money the client no longer owes.
 */
export function invoicePaid(invoice: Invoice): number {
  return invoice.paidChf + (invoice.prepaymentChf ?? 0);
}

/** The DISPLAY status of one invoice — contracts owns the rule, we only call it. */
export function invoiceStatus(invoice: Invoice, now: Date = new Date()): FinanceInvoiceStatus {
  return deriveInvoiceStatus(invoice, now);
}

/* ─────────────────────────── Client table folds ──────────────────────────── */

export interface ClientTotals {
  invoiced: number;
  paid: number;
  owed: number;
}

/**
 * Fold one client's invoices into the three money columns of their row.
 *
 * `owed` is the SERVER's `outstandingChf`, not a re-fold of the rows above it.
 * The rollup already answered that question over the same set, and a second
 * subtraction here would be a second source of truth for a number an operator
 * chases money with — they would differ the first time a row was excluded from
 * one side and not the other.
 */
export function clientTotals(client: FinanceClient): ClientTotals {
  const folded = client.invoices.reduce(
    (totals, invoice) => ({
      invoiced: totals.invoiced + invoice.totalChf,
      paid: totals.paid + invoicePaid(invoice),
    }),
    { invoiced: 0, paid: 0 },
  );
  return { ...folded, owed: client.outstandingChf };
}

/** Initials for the row avatar — first letter of up to two name words. */
export function clientInitials(name: string): string {
  return name
    .split(' ')
    .map((word) => word[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/** Which tabs a client status belongs to (artboard `inTab`). */
export function clientInTab(status: FinanceClientStatus, tab: FinanceTab): boolean {
  switch (tab) {
    case 'all':
      return true;
    case 'due':
      return status === 'overdue' || status === 'partial' || status === 'pending';
    case 'overdue':
      return status === 'overdue';
    case 'clear':
      return status === 'paid' || status === 'none';
  }
}

export const FINANCE_TABS: FinanceTab[] = ['all', 'due', 'overdue', 'clear'];

/** Row count per tab, folded from the SAME array the rows are drawn from. */
export function tabCounts(clients: FinanceClient[]): Record<FinanceTab, number> {
  const counts: Record<FinanceTab, number> = { all: 0, due: 0, overdue: 0, clear: 0 };
  for (const client of clients) {
    for (const tab of FINANCE_TABS) {
      if (clientInTab(client.status, tab)) counts[tab] += 1;
    }
  }
  return counts;
}

/**
 * Match a client against the search box: their name, or any of their invoice
 * numbers (artboard — the placeholder says «Клієнт або № рахунку» and means it).
 *
 * PROVISIONAL numbers are excluded from the haystack. They are never printed,
 * so a hit on one would highlight a row for a reason the operator cannot see.
 */
export function clientMatchesQuery(client: FinanceClient, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const numbers = client.invoices
    .map((invoice) => invoice.no)
    .filter((no) => !isProvisionalInvoiceNo(no));
  return `${client.name} ${numbers.join(' ')}`.toLowerCase().includes(needle);
}

/**
 * The counts behind the stat-card sublines.
 *
 * MONEY IS NOT HERE. `receivableChf`, `overdueChf`, `depositsHeldChf` and
 * `revenueChf` come from `GET /admin/finance/summary`, which is the endpoint
 * that exists to answer them over the whole platform. Re-folding them from the
 * client list would produce a second total that disagrees with the first the
 * moment either side filters a row — and the card would be the one an operator
 * believes. What the summary does NOT carry is the population behind each
 * figure ("3 клієнти", "1 рахунок · Andrii T. · 5 днів"), so that is folded
 * here, from the same rows the table draws.
 */
export interface ClientRollupStats {
  /** How many clients have a deposit with us at all. */
  depositClients: number;
  /** Clients with something outstanding — the «До оплати» tab's population. */
  dueClients: number;
  /** How many individual invoices are past their due date right now. */
  overdueInvoices: number;
  /** The client behind the oldest overdue invoice, for the card's subline. */
  overdueClient: string | null;
  /** How many days that invoice is past due. */
  overdueDays: number;
  /** Clients whose deposit is queued to go back to them. */
  refundClients: number;
}

export function clientRollupStats(
  clients: FinanceClient[],
  now: Date = new Date(),
): ClientRollupStats {
  const stats: ClientRollupStats = {
    depositClients: 0,
    dueClients: 0,
    overdueInvoices: 0,
    overdueClient: null,
    overdueDays: 0,
    refundClients: 0,
  };

  for (const client of clients) {
    if (client.depositChf > 0) stats.depositClients += 1;
    if (client.status === 'refund') stats.refundClients += 1;
    if (clientInTab(client.status, 'due')) stats.dueClients += 1;

    for (const invoice of client.invoices) {
      if (invoiceStatus(invoice, now) !== 'overdue') continue;
      stats.overdueInvoices += 1;
      const days = overdueDays(invoice.dueAt, now);
      if (days >= stats.overdueDays) {
        stats.overdueDays = days;
        stats.overdueClient = client.name;
      }
    }
  }

  return stats;
}

/**
 * «Найближчі надходження» — the next unpaid invoices, in the order they land.
 *
 * DERIVED from the client rollup, because there is no `/receivables` endpoint:
 * `FinanceReceivableSchema` exists in contracts but nothing serves it, and the
 * rollup already carries every unpaid invoice with its `dueAt` and its client.
 * Deliberately NOT the same view as the balances table above it — that table is
 * per client, this list is per invoice and sorted by date, so one client can
 * appear twice. An operator chasing money works down a calendar.
 *
 * Provisional rows are INCLUDED — a client really does owe that money — but
 * they carry a `PROV-` number the caller must not print; see
 * {@link isProvisionalInvoiceNo}.
 */
export function upcomingReceivables(
  clients: FinanceClient[],
  limit = 4,
  now: Date = new Date(),
): FinanceReceivable[] {
  const rows: FinanceReceivable[] = [];
  for (const client of clients) {
    for (const invoice of client.invoices) {
      if (invoiceStatus(invoice, now) === 'paid') continue;
      const amountChf = invoiceOwed(invoice);
      if (amountChf <= 0) continue;
      rows.push({
        invoiceNo: invoice.no,
        userId: client.userId,
        clientName: client.name,
        amountChf,
        dueAt: invoice.dueAt,
      });
    }
  }
  return rows.sort((a, b) => a.dueAt.getTime() - b.dueAt.getTime()).slice(0, limit);
}

/* ────────────────────────────── Revenue ──────────────────────────────────── */

/**
 * Which months a revenue period covers, as indices into the month array.
 *
 * Derived from the array length rather than a literal window, so a seventh
 * month of data shifts the window instead of silently charting August as
 * "this month".
 */
export function periodMonthIndices(months: RevenueMonth[], period: FinancePeriod): number[] {
  const span = period === 'month' ? 1 : period === 'quarter' ? 3 : months.length;
  const start = Math.max(0, months.length - span);
  return months.slice(start).map((_, offset) => start + offset);
}

/**
 * The actual `[first, last]` month keys `indices` selects out of `months`.
 *
 * `GET /admin/finance/summary?period=` uses CALENDAR windows (year = Jan 1..
 * now, quarter = the quarter's own first day..now), while `months` here is
 * always fetched as a fixed trailing window ending at the current month
 * (`REVENUE_MONTHS`, a chart-shape constant, not a period). `periodMonthIndices`
 * approximates the period by slicing the trailing end of that fixed window, so
 * for `quarter`/`year` the SET OF MONTHS it selects can differ from the
 * calendar window the summary card's same-named period covers (e.g. in October
 * `quarter` here is Aug–Oct while the card's «Квартал» is Oct alone).
 *
 * Rather than label the revenue card with the period's calendar name — which
 * would then be describing a different span than the figure beside it — the
 * card is labelled with what THIS function returns: the true span of the
 * months that were actually folded into `revenueTotals`/`revenueBars` for
 * that selection. Label and figure can then never disagree, because both are
 * read off the same `indices`.
 */
export function revenueWindowRange(
  months: RevenueMonth[],
  indices: number[],
): { first: MonthKey; last: MonthKey } | null {
  const firstIndex = indices[0];
  const lastIndex = indices[indices.length - 1];
  if (firstIndex === undefined || lastIndex === undefined) return null;
  const first = months[firstIndex];
  const last = months[lastIndex];
  if (!first || !last) return null;
  return { first: first.month, last: last.month };
}

/**
 * Per-stream revenue over the selected months, SETTLED and OUTSTANDING kept
 * apart.
 *
 * The tuple order is the legend's order — retail service fee, dealer cut,
 * dealer subscription, then the deposit forfeited on a refused car (owner
 * decision (a); absent from the artboard, so the card lists it only when it is
 * non-zero) — and it is a different order from
 * `RevenueByStream`'s field order on purpose: the legend is the artboard's and
 * the contract's is alphabetical-ish. Mapping once, here, is what stops a
 * component from indexing the wrong stream.
 */
export interface RevenueTotals {
  /** Invoiced in the window and settled since. */
  received: [number, number, number, number];
  /** Invoiced in the window and still outstanding. */
  pending: [number, number, number, number];
  receivedTotal: number;
  pendingTotal: number;
  /**
   * Everything the CHART covers: received + pending across the window.
   *
   * This is NOT the summary card's `revenueChf` and must not be labelled as if
   * it were. Two honest differences: the card's rollup keeps an undated
   * invoiced deal (dating it now) where the chart drops it for having no
   * bucket, and `dealer_subscription` is a chart stream with no counterpart in
   * the card's figure at all. Labelling both "Дохід" is how an operator reads
   * the gap as a bug.
   */
  chartedTotal: number;
}

export function revenueTotals(months: RevenueMonth[], indices: number[]): RevenueTotals {
  const received: [number, number, number, number] = [0, 0, 0, 0];
  const pending: [number, number, number, number] = [0, 0, 0, 0];

  for (const index of indices) {
    const month = months[index];
    if (!month) continue;
    received[0] += month.received.retailServiceChf;
    received[1] += month.received.dealerCutChf;
    received[2] += month.received.dealerSubscriptionChf;
    received[3] += month.received.depositForfeitChf ?? 0;
    pending[0] += month.pending.retailServiceChf;
    pending[1] += month.pending.dealerCutChf;
    pending[2] += month.pending.dealerSubscriptionChf;
    pending[3] += month.pending.depositForfeitChf ?? 0;
  }

  const cents = (value: number): number => Math.round(value * 100) / 100;
  const sum = (values: readonly number[]): number => values.reduce((acc, v) => acc + v, 0);
  const receivedTotal = cents(sum(received));
  const pendingTotal = cents(sum(pending));
  return {
    received: received.map(cents) as [number, number, number, number],
    pending: pending.map(cents) as [number, number, number, number],
    receivedTotal,
    pendingTotal,
    chartedTotal: cents(receivedTotal + pendingTotal),
  };
}

/** The whole bar for one month — received + pending, per stream. */
export function monthTotal(month: RevenueMonth): number {
  const totals = revenueMonthTotals(month);
  return (
    Math.round(
      (totals.retailServiceChf +
        totals.dealerCutChf +
        totals.dealerSubscriptionChf +
        (totals.depositForfeitChf ?? 0)) *
        100,
    ) / 100
  );
}

/** Tallest bar in the chart, in px — the artboard's 140px box minus its labels. */
export const REVENUE_BAR_MAX_PX = 96;

export interface RevenueBar {
  /** `YYYY-MM`; the component resolves the caption from the catalogue. */
  month: MonthKey;
  /** Month total (received + pending), for the cap above the bar. */
  total: number;
  /** SETTLED segment heights in px, bottom-up: retail, dealer cut, subscription. */
  retailPx: number;
  dealerPx: number;
  subscriptionPx: number;
  /** SETTLED deposit forfeited on a refused car (owner decision (a)), px. */
  forfeitPx: number;
  /** Everything invoiced in this month and still outstanding, as one px band. */
  pendingPx: number;
  /** True when this month is inside the selected period. */
  selected: boolean;
}

/**
 * Bar geometry.
 *
 * THE SOLID BAR IS `received`. `RevenueMonth` is not a flat figure per stream —
 * it is a settlement split WITHIN the issue month, so drawing the sum as one
 * solid block would tell an operator that money has landed when it has only
 * been invoiced. The outstanding part is drawn as one muted band on top and the
 * cap prints the month TOTAL, which comes from the contract's own
 * `revenueMonthTotals` rather than being re-added here — one definition of the
 * total, one rounding rule.
 *
 * The scale is set by the TALLEST month in the whole series, not by the
 * selected window: switching «Вересень» → «Квартал» must not rescale the chart
 * under the operator, or two readings of the same bar disagree.
 */
export function revenueBars(months: RevenueMonth[], indices: number[]): RevenueBar[] {
  const totals = months.map(monthTotal);
  const max = Math.max(...totals, 1);
  const scale = REVENUE_BAR_MAX_PX / max;
  const selected = new Set(indices);

  return months.map((month, index) => {
    const pending =
      month.pending.retailServiceChf +
      month.pending.dealerCutChf +
      month.pending.dealerSubscriptionChf +
      (month.pending.depositForfeitChf ?? 0);
    return {
      month: month.month,
      total: totals[index] ?? 0,
      retailPx: Math.round(month.received.retailServiceChf * scale),
      dealerPx: Math.round(month.received.dealerCutChf * scale),
      subscriptionPx: Math.round(month.received.dealerSubscriptionChf * scale),
      forfeitPx: Math.round((month.received.depositForfeitChf ?? 0) * scale),
      pendingPx: Math.round(pending * scale),
      selected: selected.has(index),
    };
  });
}

/* ── Design D tone tables ───────────────────────────────────────────────────
 * Artboard `FST` / `IST` and the transaction kind map, expressed as cf- token
 * classes. Colours live here and nowhere else in the feature, so a status that
 * means "urgent" is the same red in the chip, the stripe and the amount.
 * ────────────────────────────────────────────────────────────────────────── */

/** Chip background + foreground per client status. */
export const CLIENT_STATUS_CHIP: Record<FinanceClientStatus, string> = {
  overdue: 'bg-cf-danger-tint text-cf-danger',
  partial: 'bg-cf-warn-tint text-cf-warn',
  pending: 'bg-cf-fill text-cf-ink-muted',
  refund: 'bg-cf-info-tint text-cf-info-ink',
  paid: 'bg-cf-success-tint text-cf-success',
  none: 'bg-cf-fill text-cf-ink-subtle',
};

/** The 3px inset rule down the row's left edge — the artboard's `stripe`. */
export const CLIENT_STATUS_STRIPE: Record<FinanceClientStatus, string> = {
  overdue: 'shadow-[inset_3px_0_0_var(--color-cf-danger)]',
  partial: 'shadow-[inset_3px_0_0_var(--color-cf-accent)]',
  pending: 'shadow-[inset_3px_0_0_var(--color-cf-line)]',
  refund: 'shadow-[inset_3px_0_0_var(--color-cf-info)]',
  paid: 'shadow-[inset_3px_0_0_var(--color-cf-success)]',
  none: 'shadow-[inset_3px_0_0_var(--color-cf-line)]',
};

/** i18n key for a client status label. */
export function clientStatusKey(status: FinanceClientStatus): string {
  return `adminFinance.clientStatus.${status}`;
}

/**
 * Per-invoice status colour inside the expanded row. `checking` («Платіж
 * перевіряється») is INFO, not warn or danger: the client says they paid, so
 * the operator's job is to look, not to chase.
 */
export const INVOICE_STATUS_TEXT: Record<FinanceInvoiceStatus, string> = {
  overdue: 'text-cf-danger',
  checking: 'text-cf-info-ink',
  pending: 'text-cf-warn',
  partial: 'text-cf-warn',
  paid: 'text-cf-success',
  // Decision (a): the client refused to pay and the deposit was withheld.
  cancelled: 'text-cf-ink-subtle',
};

/** i18n key for an invoice status label. */
export function invoiceStatusKey(status: FinanceInvoiceStatus): string {
  return `adminFinance.invoiceStatus.${status}`;
}

/** i18n key for what an invoice bills for. */
export function invoiceKindKey(kind: InvoiceKind): string {
  return `adminFinance.invoiceKind.${kind}`;
}

/** Amount colour in the journal: money in is green, refunds blue, drafts ink. */
export const TRANSACTION_AMOUNT_TEXT: Record<TransactionKind, string> = {
  in: 'text-cf-success',
  out: 'text-cf-info-ink',
  issued: 'text-cf-ink',
};

/** Journal status chip per kind. */
export const TRANSACTION_CHIP: Record<TransactionKind, string> = {
  in: 'bg-cf-success-tint text-cf-success',
  out: 'bg-cf-info-tint text-cf-info-ink',
  issued: 'bg-cf-fill text-cf-ink-muted',
};

/**
 * i18n key for a journal status label.
 *
 * `out` is «Виплачено», not «Очікує виплати» (BUG-11): every `out` row the
 * journal can show is a RECORDED ledger entry (`payments` type `refund`, or a
 * debit `adjustment`) — the money has already left. The journal has no
 * «requested, not yet paid» source (`journal.service.ts` merges the ledger and
 * the invoices, nothing else), so a pending-payout label would never be true.
 */
const TRANSACTION_STATUS_KEY: Record<TransactionKind, string> = {
  in: 'adminFinance.transactionStatus.in',
  out: 'adminFinance.transactionStatus.paidOut',
  issued: 'adminFinance.transactionStatus.issued',
};

export function transactionStatusKey(kind: TransactionKind): string {
  return TRANSACTION_STATUS_KEY[kind];
}

/**
 * A journal amount with its sign and its own currency.
 *
 * The contract carries a non-negative MAGNITUDE and puts the direction on
 * `kind`, precisely so a renderer cannot negate an already-negative number and
 * silently invert an outgoing payment. `issued` gets no sign at all: it is a
 * receivable, not a movement — nothing has gone anywhere.
 */
export function transactionAmount(tx: FinanceTransaction): string {
  const amount = formatMoney(tx.amount, tx.currency);
  if (tx.kind === 'in') return `+${amount}`;
  if (tx.kind === 'out') return `−${amount}`;
  return amount;
}

/** Dot colour on the upcoming-receivables list. */
export const RECEIVABLE_DOT: Record<ReceivableTone, string> = {
  danger: 'bg-cf-danger',
  warn: 'bg-cf-warn',
  neutral: 'bg-cf-ink-muted',
};

/** Date colour on the same list — neutral rows mute the date, urgent ones don't. */
export const RECEIVABLE_TEXT: Record<ReceivableTone, string> = {
  danger: 'text-cf-danger',
  warn: 'text-cf-warn',
  neutral: 'text-cf-ink-subtle',
};

/**
 * Amount colour in the «До доплати» column: red when the client is late, amber
 * when they merely owe, muted when there is nothing to chase.
 */
export function owedTextClass(status: FinanceClientStatus, owed: number): string {
  if (status === 'overdue') return 'text-cf-danger';
  return owed ? 'text-cf-warn' : 'text-cf-ink-subtle';
}

/** Deposit colour: blue while it is queued to go back to the client. */
export function depositTextClass(status: FinanceClientStatus, deposit: number): string {
  if (status === 'refund') return 'text-cf-info-ink';
  return deposit ? 'text-cf-ink' : 'text-cf-ink-subtle';
}

/**
 * Which row action, if any, the operator is offered.
 *
 * `'refund'` used to be returned for `status === 'refund'`, but
 * `resolveClientStatus` never emits that status (see the note on
 * `FinanceTab`), so the branch was dead by construction and is gone. The
 * remaining action, `'remind'`, posts to
 * `POST /admin/finance/invoices/:no/remind` for the invoice
 * {@link remindTarget} picks — and is disabled when it picks none.
 */
export function rowAction(status: FinanceClientStatus): 'remind' | null {
  if (status === 'overdue' || status === 'partial' || status === 'pending') return 'remind';
  return null;
}

/** The server's reminder throttle: one per invoice per 24 h (`REMIND_TOO_SOON`). */
export const REMIND_THROTTLE_MS = 24 * 60 * 60 * 1000;

/**
 * The invoice a row's «Нагадати» reminds the client about, or null.
 *
 * Candidates are the documents the server would accept a reminder for AND an
 * operator would want one sent for:
 * - never a `PROV-` row — no such invoice exists, the endpoint answers 404;
 * - never `paid` / `cancelled`, nor one that owes nothing — 409
 *   `INVOICE_ALREADY_PAID`;
 * - never `checking` — the client has already said «Я сплатив», and the deals
 *   screen withholds «Нагадати» on that status for the same reason.
 *
 * Of those, the EARLIEST-DUE one: it is the most overdue (or the next to fall
 * due), which is what the row's status chip and deadline phrase describe, so
 * the reminder is about the debt the operator is looking at. Ties fall to the
 * earlier-issued invoice.
 */
export function remindTarget(client: Pick<FinanceClient, 'invoices'>): Invoice | null {
  const open = client.invoices.filter(
    (invoice) =>
      !isProvisionalInvoiceNo(invoice.no) &&
      invoice.status !== 'paid' &&
      invoice.status !== 'cancelled' &&
      invoice.status !== 'checking' &&
      invoiceOutstandingChf(invoice) > 0,
  );
  if (!open.length) return null;
  return open.reduce((best, invoice) => {
    const byDue = invoice.dueAt.getTime() - best.dueAt.getTime();
    if (byDue !== 0) return byDue < 0 ? invoice : best;
    return invoice.issuedAt.getTime() < best.issuedAt.getTime() ? invoice : best;
  });
}

/**
 * When the invoice was last reminded, if that is still inside the 24 h
 * throttle — the server would answer 429 `REMIND_TOO_SOON` to another press,
 * so the row shows «нагадано …» instead of the button.
 */
export function recentReminder(
  remindedAt: Date | null | undefined,
  now: Date = new Date(),
): Date | null {
  if (!remindedAt) return null;
  return now.getTime() - remindedAt.getTime() < REMIND_THROTTLE_MS ? remindedAt : null;
}

/** i18n key for a client's tariff caption — rendered from `tier`, never sent. */
export function tierKey(tier: PricingPlanTier): string {
  return `adminFinance.tier.${tier}`;
}

/* ── Invoice drafting ───────────────────────────────────────────────────────
 * The arithmetic behind the «Виставити рахунок» drawer, kept out of the
 * component so it can be tested without rendering anything.
 *
 * EVERY RATE IS DATA. Not one fee, FX rate, customs percentage or subscription
 * price is named in this file: the drawer is handed the live `PricingRuleSet`
 * (`GET /api/pricing/rules/current`) and asks `computeClientDebt` — the SAME
 * function that produces the stored debt breakdown when a relayed bid is marked
 * won, and the same one `InvoicesService` prices a car invoice with.
 *
 * PER-DEAL LINES SEED AT 0. Transport, excise and a surcharge carry the basis
 * key `…basis.perDeal` and start empty, because the rule set has no equivalent
 * for any of them and cannot: transport is quoted per route, excise needs an
 * engine displacement the lot record does not carry, and a surcharge is by
 * definition whatever the customs office came back with. A wrong non-zero
 * default is worse than an empty field — an operator corrects a blank and signs
 * off on a number.
 *
 * The customs base is NOT this file's to define: transport sits INSIDE the
 * customs value and therefore inside the duty, VAT and pension-fund base.
 * `computeLandedCost` (contracts/landed-cost.ts) is where that base is written
 * down — `bid + transport`. The Swiss platform fee is NOT in it and NOT billed:
 * CarsFinders pays it out of its commission (owner, 2026-09-28).
 *
 * CURRENCY (owner rule 01). A CarsFinders invoice is a CHF document. The CHF
 * leg of the debt breakdown (the hammer price) and every
 * figure an operator types are CHF and travel with `fx: null`. The two
 * EUR-priced figures — the CarsFinders service fee (`computeClientDebt`) and a
 * plan's `monthlyFee` — are converted ONCE via `convertEurToChf` at the rule
 * set's own `swissExport.chfToEurRate`, and the converted line records the
 * conversion beside its value. An operator override replaces the converted
 * figure with a typed CHF one, and its `fx` goes back to `null` — the recorded
 * conversion would no longer reproduce the value.
 *
 * The customs lines (duty, VAT, pension fund) are computed on the CHF customs
 * value directly. Only the pension fund's TIER is picked on the EUR customs
 * value, because the statute's thresholds are written in EUR — a threshold
 * comparison, not a conversion of any line's value.
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * The drawer's «Депозит зараховано −CHF …» line — a PREVIEW. The server
 * computes the real prepayment with the same `prepaymentFor` inside the
 * issuing transaction, against the deposit it reads there; this only shows
 * the operator what that will be.
 *
 * `totalChf: null` means the invoice is priced server-side (a car invoice
 * opened from a deal) and its total is not known here: the preview then says
 * "the whole deposit, capped at the invoice total" (`upTo`) and names no
 * amount to pay, rather than inventing a total to subtract from.
 */
export interface PrepaymentPreview {
  prepaymentChf: number;
  /** True when the total is unknown, so the figure is a ceiling. */
  upTo: boolean;
  /** Total − prepayment, or null when the total is unknown. */
  toPayChf: number | null;
}

export function prepaymentPreview(depositChf: number, totalChf: number | null): PrepaymentPreview {
  if (totalChf === null) {
    return {
      prepaymentChf: prepaymentFor(depositChf, Math.max(0, depositChf)).prepaymentChf,
      upTo: true,
      toPayChf: null,
    };
  }
  const { prepaymentChf } = prepaymentFor(depositChf, totalChf);
  const toPayChf = Math.round(Math.max(0, totalChf - prepaymentChf) * 100) / 100;
  return { prepaymentChf, upTo: false, toPayChf };
}

/** Payment windows the drawer offers, in days. */
export const INVOICE_DUE_DAYS = [3, 7, 14] as const;

/** The `Date` a due-day chip means. */
export function dueDate(days: number, from: Date = new Date()): Date {
  const due = new Date(from.getTime());
  due.setDate(due.getDate() + days);
  return due;
}

/** `dd.MM`, `days` from `from` — the drawer's due-date chips. */
export function dueDateLabel(days: number, from: Date = new Date()): string {
  return formatDayMonth(dueDate(days, from));
}

/*
 * The pension-fund rate used to be re-implemented HERE. It is not any more: the
 * canonical `pensionFundPct` is exported from `@carsfinders/contracts`
 * (landed-cost.ts) and imported above. The admin copy diverged on the one case
 * that matters — a ladder whose last tier is BOUNDED — and priced the same car
 * two ways inside one product.
 */

export interface InvoiceDraft {
  lines: InvoiceLine[];
  total: number;
  /** The part of `total` that is CarsFinders revenue. */
  ours: number;
}

export interface InvoiceDraftInput {
  kind: InvoiceKind;
  /**
   * The client's plan tier — what their commission is priced under. NOT a
   * `dealer` boolean: the three tiers charge three different success fees and
   * only some carry a subscription.
   */
  tier: PricingPlanTier;
  lot: InvoiceLot | null;
  /** Operator's free-text description, for the surcharge/other kinds. */
  description: string;
  /** Per-line manual overrides, keyed by line key. */
  overrides: Record<string, number>;
}

/** One item out of a computed debt breakdown, or 0 when the code is absent. */
function debtAmount(
  items: ReadonlyArray<{ code: string; amount: number }>,
  code: string,
): number {
  return items.find((item) => item.code === code)?.amount ?? 0;
}

/**
 * Compose the line items of a draft invoice against the rule set in force.
 *
 * `rules` is the FIRST parameter because it is the authority: every number
 * below is either read off it or derived from one that was. There is no
 * fallback — a caller without a rule set has nothing to quote, and quoting the
 * package defaults would put an unpublished tariff on a money demand.
 *
 * Every derived line is computed from the lines ABOVE it, so an override
 * ripples: correcting the winning bid re-bases duty, VAT and the pension fund
 * the way the customs office would. An overridden line stops being derived —
 * that is the point of the override.
 */
export function buildInvoiceDraft(rules: PricingRuleSet, input: InvoiceDraftInput): InvoiceDraft {
  const { kind, tier, lot, description, overrides } = input;
  const value = (key: string, derived: number): number =>
    overrides[key] !== undefined ? overrides[key] : derived;

  let lines: InvoiceLine[] = [];

  const chfToEur = rules.swissExport.chfToEurRate;
  const ratesVersion = rules.customs.version;

  /**
   * A EUR-priced figure as a CHF line value + its `fx`, honouring an override.
   * An overridden line is a CHF figure the operator typed: `fx: null`.
   *
   * Whole francs, like every other figure the drawer quotes (its inputs take
   * digits only); `fx` keeps the exact conversion, so `fromAmount × rate`
   * still reproduces the value to within half a franc.
   */
  const converted = (key: string, amountEur: number): Pick<InvoiceLine, 'valueChf' | 'fx'> => {
    if (overrides[key] !== undefined) return { valueChf: overrides[key], fx: null };
    const conversion = convertEurToChf(amountEur, chfToEur, ratesVersion);
    // `validateRuleSet` refuses a non-positive rate, so a null here means the
    // rule set is unusable — a 0 line keeps submit disabled rather than
    // quoting a guessed figure.
    if (conversion === null) return { valueChf: 0, fx: null };
    return { valueChf: Math.round(conversion.valueChf), fx: conversion.fx };
  };

  if (kind === 'car' && lot) {
    // The bid line FIRST, because everything below is a function of it. An
    // operator correcting the winning bid is correcting the base of the whole
    // stack — including our own commission, a ladder over the bid, not a
    // constant. CHF at the source.
    const bid = value('bid', Math.round(lot.bidChf));

    // THE calculation — the same one that bills the client when the bid is
    // marked won, so the drawer and the stored breakdown cannot disagree.
    // It carries no Swiss platform fee: CarsFinders pays that out of its
    // commission, so there is no fee line and it is not in the customs value.
    const debt = computeClientDebt(rules, tier, bid);

    const transport = value('transport', 0);
    // The CUSTOMS VALUE, and the one base three of the four charges below are
    // measured on: `computeLandedCost` defines it as `bid + transport`.
    const customsValue = bid + transport;
    const dutyPct = rules.customs.dutyPct;
    const duty = value('duty', Math.round(dutyPct * customsValue));
    const excise = value('excise', 0);
    const vatPct = rules.customs.vatPct;
    const vat = value('vat', Math.round(vatPct * (customsValue + duty + excise)));
    // The statute's pension-fund thresholds are EUR, so the TIER is picked on
    // the EUR customs value; the amount is that rate over the CHF base.
    const pensionPct = pensionFundPct(customsValue * chfToEur, rules);
    const pension = value('pension', Math.round(pensionPct * customsValue));
    const service = converted('service', debtAmount(debt.items, 'service_fee'));

    lines = [
      {
        key: 'bid',
        labelKey: 'adminFinance.drawer.line.bid',
        basisKey: 'adminFinance.drawer.basis.bid',
        valueChf: bid,
        fx: null,
        ours: false,
      },
      {
        key: 'transport',
        labelKey: 'adminFinance.drawer.line.transport',
        basisKey: 'adminFinance.drawer.basis.perDeal',
        valueChf: transport,
        fx: null,
        ours: false,
      },
      {
        key: 'duty',
        labelKey: 'adminFinance.drawer.line.duty',
        basisKey: 'adminFinance.drawer.basis.duty',
        basisParams: { pct: formatPct(dutyPct) },
        valueChf: duty,
        fx: null,
        ours: false,
      },
      {
        key: 'excise',
        labelKey: 'adminFinance.drawer.line.excise',
        basisKey: 'adminFinance.drawer.basis.perDeal',
        valueChf: excise,
        fx: null,
        ours: false,
      },
      {
        key: 'vat',
        labelKey: 'adminFinance.drawer.line.vat',
        basisKey: 'adminFinance.drawer.basis.vat',
        basisParams: { pct: formatPct(vatPct) },
        valueChf: vat,
        fx: null,
        ours: false,
      },
      {
        key: 'pension',
        labelKey: 'adminFinance.drawer.line.pension',
        basisKey: 'adminFinance.drawer.basis.pension',
        basisParams: { pct: formatPct(pensionPct) },
        valueChf: pension,
        fx: null,
        ours: false,
      },
      {
        key: 'service',
        labelKey: 'adminFinance.drawer.line.service',
        // A tier-specific KEY rather than a `{tier}` param, because utils.ts is
        // pure and must not reach into the catalogue to name a tier. `rate` is
        // the EUR-per-CHF figure the commission was converted back at.
        basisKey: `adminFinance.drawer.basis.service.${tier}`,
        basisParams: { base: formatNumber(bid), rate: formatNumber(chfToEur) },
        ...service,
        ours: true,
      },
    ];
  } else if (kind === 'sub') {
    // The subscription is the tier's `monthlyFee` and nothing else — priced in
    // EUR by the tariff, billed in CHF. Under a grid where the tier carries
    // none this is 0, the submit button stays disabled, and that is correct: a
    // client on a plan with no subscription cannot be billed one.
    const plan = resolvePlanForTier(rules.plans, tier);
    const subscription = converted('amount', plan.monthlyFee);
    lines = [
      {
        key: 'amount',
        labelKey: 'adminFinance.drawer.line.subscription',
        basisKey: `adminFinance.drawer.basis.subscription.${tier}`,
        basisParams: { rate: formatNumber(chfToEur) },
        ...subscription,
        ours: true,
      },
    ];
  } else if (kind !== 'car') {
    lines = [
      {
        key: 'amount',
        label: description.trim() || undefined,
        labelKey: 'adminFinance.drawer.line.noDescription',
        basisKey: 'adminFinance.drawer.basis.perDeal',
        valueChf: value('amount', 0),
        fx: null,
        ours: false,
      },
    ];
  }

  return {
    lines,
    total: lines.reduce((sum, line) => sum + line.valueChf, 0),
    ours: lines.filter((line) => line.ours).reduce((sum, line) => sum + line.valueChf, 0),
  };
}

/**
 * Which invoice kinds a client can be billed for.
 *
 * The subscription line is offered only when the client's PLAN actually carries
 * a `monthlyFee`. Offering it to every dealer is how the drawer came to quote
 * 500/month against a grid whose `dealer` tier charges no subscription at
 * all.
 */
export function invoiceKindsFor(plan: PricingPlan): InvoiceKind[] {
  return plan.monthlyFee > 0 ? ['car', 'extra', 'sub', 'other'] : ['car', 'extra', 'other'];
}
